import * as React from "react"
import { useUser } from "@/context/UserContext"
import { ROLES } from "@/lib/roles"
import { fetchActivity, fetchConnection } from "../api/connectionsClient"
import { useCatalog } from "./useCatalog"
import type { ActivityEvent, Connection, IntegrationCatalogItem } from "../api/types"

export type IntegrationState = {
  isLoading: boolean
  error: Error | null
  item: IntegrationCatalogItem | null
  connection: Connection | null
  activity: ActivityEvent[]
}

const INITIAL: IntegrationState = {
  isLoading: true,
  error: null,
  item: null,
  connection: null,
  activity: [],
}

export function useIntegration(integrationId: string): IntegrationState {
  const catalog = useCatalog()
  const { user } = useUser()
  const showMockConnections = user?.roleId === ROLES.DEVELOPER
  const [state, setState] = React.useState<IntegrationState>(INITIAL)

  React.useEffect(() => {
    if (catalog.isLoading) return
    if (catalog.error) {
      setState({ ...INITIAL, isLoading: false, error: catalog.error })
      return
    }

    const item = catalog.items.find((i) => i.id === integrationId) ?? null
    if (!item) {
      setState({ ...INITIAL, isLoading: false, error: new Error("Integration not found") })
      return
    }

    let cancelled = false
    const connectionPromise = showMockConnections
      ? fetchConnection(integrationId)
      : Promise.resolve(null)
    const activityPromise = showMockConnections
      ? fetchActivity(integrationId)
      : Promise.resolve([] as ActivityEvent[])

    Promise.all([connectionPromise, activityPromise])
      .then(([connection, activity]) => {
        if (cancelled) return
        setState({ isLoading: false, error: null, item, connection, activity })
      })
      .catch((err: Error) => {
        if (cancelled) return
        setState({ ...INITIAL, isLoading: false, error: err })
      })

    return () => {
      cancelled = true
    }
  }, [integrationId, catalog.isLoading, catalog.error, catalog.items, showMockConnections])

  return state
}
